import DefaultLayout from "../layouts/DefaultLayout";
import SortableDataTable from "../components/SortableDataTable";
import {useCallback, useEffect, useMemo, useState} from "react";
import ConfirmationModal from "./modals/ConfirmationModal";
import useTranslation from "../hooks/translations";
import CostTypeModal from "./modals/CostTypeModal";
import {TrendingDown} from "react-feather";
import {useApi} from "../contexts/ApiProvider";
import CostTypeService from "../services/CostTypeService";
import ProtectedContent from "../components/ProtectedContent";

export default function CostTypeSettingsPage() {
  const translations = useTranslation();
  const columns = [
    {
      name: translations.name,
      data_identifier: "name"
    },
    {
      name: translations.description,
      data_identifier: "description"
    },
    {
      name: translations.calculateForGross,
      data_identifier: "calculateForGrossFormatted"
    },
    {
      name: translations.calculateForNet1,
      data_identifier: "calculateForNet1Formatted"
    },
    {
      name: translations.calculateForNet2,
      data_identifier: "calculateForNet2Formatted"
    }
  ];
  const pageTitle = translations.costType
  const pageSubtitle = translations.costTypePageSubtitle
  const api = useApi()
  const costTypeService = useMemo(() => new CostTypeService(api), [api]);

  const [openCostTypeModal, setOpenCostTypeModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [rows, setRows] = useState([]);

  const getData = useCallback(async () => {
    const response = await costTypeService.getAll();
    const formatedResponse = response.body.map((row) => {
      return {
        id: row.id,
        name: row.name,
        description: row.description,
        order: row.order,
        calculateForGross: row.calculate_for_gross,
        calculateForGrossFormatted: row.calculate_for_gross ? translations.yes : translations.no,
        calculateForNet1: row.calculate_for_net_1,
        calculateForNet1Formatted: row.calculate_for_net_1 ? translations.yes : translations.no,
        calculateForNet2: row.calculate_for_net_2,
        calculateForNet2Formatted: row.calculate_for_net_2 ? translations.yes : translations.no,
      }
    });
    setRows(formatedResponse);
  }, [costTypeService, translations.yes, translations.no])

  const reorderData = useCallback(async (data) => {
    const formatedRequest = data.map((row) => {
      return {
        id: row.id,
        order: data.indexOf(row) + 1
      }
    });
    await costTypeService.reorder(formatedRequest);
  }, [costTypeService])

  useEffect(() => {
    (async () => {
      await getData()
    })();
  }, [getData]);

  // datatable callbacks
  const onEditClick = async (data) => {
    setSelectedRow(data)
    setOpenCostTypeModal(true)
  }

  const onDeleteClick = async (data) => {
    setSelectedRow(data)
    setOpenConfirmationModal(true)
  }

  const onAddClick = async () => {
    setOpenCostTypeModal(true)
    setSelectedRow({})
  }

  const handleDragEnd = async (data) => {
    setRows(data)
    await reorderData(data)
  }

  // cost type modal callbacks
  const onModalSubmit = async (data) => {
    await getData()
    setOpenCostTypeModal(false)
  }

  const onModalClose = async () => {
    setOpenCostTypeModal(false)
  }

  // confirmation modal callbacks
  const onConfirmationModalSubmit = async (data) => {
    await costTypeService.delete(data.id)
    await getData()
    setOpenConfirmationModal(false)
  }

  const onConfirmationModalClose = async () => {
    setOpenConfirmationModal(false)
  }

  return (
    <DefaultLayout icon={<TrendingDown/>}
                   title={pageTitle}
                   subtitle={pageSubtitle}>
      <ProtectedContent
        allowedRoles={["Admin"]}
        alt={<div style={{textAlign: "center", margin: 10}}>
          <h1>{translations.permissionsAlert}</h1></div>}>
        <SortableDataTable title={pageTitle}
                           columns={columns}
                           rows={rows}
                           editAction={onEditClick}
                           deleteAction={onDeleteClick}
                           addAction={onAddClick}
                           dragEndAction={handleDragEnd}/>
        {openCostTypeModal &&
          <CostTypeModal data={selectedRow}
                         title={pageTitle}
                         onModalSubmit={onModalSubmit}
                         onModalClose={onModalClose}
                         showModal={openCostTypeModal}/>
        }
        {openConfirmationModal &&
          <ConfirmationModal data={selectedRow}
                             onConfirmationModalSubmit={onConfirmationModalSubmit}
                             onConfirmationModalClose={onConfirmationModalClose}
                             showModal={openConfirmationModal}/>
        }
      </ProtectedContent>
    </DefaultLayout>
  )
}
